import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle2,
  Star,
  Zap,
  Crown,
  Sparkles,
  TrendingUp,
  DollarSign,
  Calculator,
  ArrowRight,
  Users,
  Clock,
  Video,
  Download,
  Shield
} from 'lucide-react';

const LiveStreamPricing = () => {
  const [selectedTier, setSelectedTier] = useState('standard');

  const pricingTiers = [
    {
      id: 'basic',
      name: "Basic Events",
      price: "$29",
      originalPrice: "$39",
      description: "Perfect for small recitals and showcases",
      popular: false,
      savings: "Save $10",
      features: [
        "Up to 90 minutes streaming",
        "50 concurrent viewers",
        "30-day recording access", 
        "HD quality streaming",
        "Family email invitations",
        "Basic viewer analytics",
        "Mobile & desktop viewing",
        "Automatic cloud recording"
      ],
      limitations: [],
      bestFor: "Small dance studios, music schools",
      roi: {
        cost: "$29",
        revenue: "$1,250",
        profit: "$1,221",
        margin: "97.7%"
      }
    },
    {
      id: 'standard', 
      name: "Standard Events",
      price: "$49",
      originalPrice: "$69",
      description: "Ideal for most dance recitals and competitions",
      popular: true,
      savings: "Save $20",
      features: [
        "Up to 150 minutes streaming",
        "100 concurrent viewers",
        "60-day recording access",
        "HD quality streaming", 
        "Social sharing tools",
        "Advanced analytics dashboard",
        "Custom event branding",
        "Live chat moderation",
        "Multi-device streaming",
        "Priority technical support"
      ],
      limitations: [],
      bestFor: "Most dance studios, gymnastics centers",
      roi: {
        cost: "$49", 
        revenue: "$2,500",
        profit: "$2,451",
        margin: "98.0%"
      }
    },
    {
      id: 'premium',
      name: "Premium Events",
      price: "$75",
      originalPrice: "$99", 
      description: "For large competitions and multi-session events",
      popular: false,
      savings: "Save $24",
      features: [
        "Unlimited streaming time",
        "Unlimited viewers",
        "90-day recording access",
        "4K quality streaming option",
        "Multi-camera support",
        "Professional graphics overlay",
        "Live chat with reactions",
        "Advanced moderation tools",
        "White-label branding",
        "Dedicated account manager",
        "24/7 priority support",
        "Custom integrations"
      ],
      limitations: [],
      bestFor: "Large studios, competitions, camps",
      roi: {
        cost: "$75",
        revenue: "$3,750", 
        profit: "$3,675",
        margin: "98.0%"
      }
    }
  ];

  const addOns = [
    {
      name: "Extended Recording Access",
      price: "$15",
      description: "Keep recordings available for 6 months instead of 30-90 days"
    },
    {
      name: "Professional Highlight Reel",
      price: "$25", 
      description: "AI-generated 2-3 minute highlight reel of best moments"
    },
    {
      name: "Multi-Camera Package",
      price: "$35",
      description: "Support for up to 4 camera angles with live switching"
    },
    {
      name: "Custom Graphics Package", 
      price: "$20",
      description: "Custom lower-thirds, sponsor logos, and event branding"
    }
  ];

  const volumeDiscounts = [
    { events: "5+ events/year", discount: "10% off", savings: "$15-37" },
    { events: "10+ events/year", discount: "15% off", savings: "$44-112" },
    { events: "20+ events/year", discount: "20% off", savings: "$116-300" }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white mb-6 px-6 py-2 text-lg">
            <DollarSign className="h-5 w-5 mr-2" />
            Launch Pricing - Limited Time
          </Badge>
          <h2 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Pricing That Makes
            <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent"> Cents</span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-8">
            Turn your events into profit centers with transparent, per-event pricing. No monthly fees, no hidden costs.
          </p>
          
          {/* ROI Highlight */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-gradient-to-r from-green-100 to-emerald-100 border-2 border-green-200 rounded-2xl p-6 max-w-4xl mx-auto"
          >
            <h3 className="text-2xl font-bold text-green-800 mb-4">🚀 Explosive ROI Example</h3>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-green-700">$49</div>
                <div className="text-green-600">Your Cost</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-green-700">$2,500</div>
                <div className="text-green-600">Event Revenue</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-green-700">5,010%</div>
                <div className="text-green-600">ROI</div>
              </div>
            </div>
            <p className="text-green-700 font-medium mt-4">
              Based on 50 families × $50 each for a 2-hour dance recital
            </p>
          </motion.div>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-20">
          {pricingTiers.map((tier, index) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative"
              onClick={() => setSelectedTier(tier.id)}
            >
              {tier.popular && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                  <Badge className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 text-lg font-bold animate-pulse">
                    <Star className="h-5 w-5 mr-2 fill-current" />
                    Most Popular
                  </Badge>
                </div>
              )}
              
              <Card className={`h-full cursor-pointer transition-all duration-300 ${
                tier.popular 
                  ? 'border-3 border-blue-300 shadow-2xl scale-105 bg-gradient-to-b from-blue-50 to-purple-50' 
                  : selectedTier === tier.id
                  ? 'border-2 border-blue-200 shadow-lg'
                  : 'border border-gray-200 hover:shadow-lg hover:border-blue-100'
              }`}>
                <CardHeader className="text-center pb-8 pt-12">
                  {/* Savings Badge */}
                  <Badge className="bg-red-500 text-white mb-4 mx-auto w-fit">
                    <Sparkles className="h-4 w-4 mr-1" />
                    {tier.savings}
                  </Badge>
                  
                  <CardTitle className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</CardTitle>
                  
                  {/* Pricing */}
                  <div className="mb-4">
                    <div className="flex items-center justify-center gap-3">
                      <span className="text-2xl text-gray-400 line-through">{tier.originalPrice}</span>
                      <span className="text-5xl font-bold text-gray-900">{tier.price}</span>
                    </div>
                    <span className="text-gray-600 text-lg">/ event</span>
                  </div>
                  
                  <p className="text-gray-600 mb-6">{tier.description}</p>
                  
                  {/* ROI Box */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="text-sm text-green-600 font-medium mb-2">Potential ROI:</div>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <div className="text-green-800 font-bold">{tier.roi.revenue}</div>
                        <div className="text-green-600">Revenue</div>
                      </div>
                      <div>
                        <div className="text-green-800 font-bold">{tier.roi.margin}</div>
                        <div className="text-green-600">Margin</div>
                      </div>
                    </div>
                  </div>

                  <div className="text-sm text-gray-500 mb-6">
                    <strong>Best for:</strong> {tier.bestFor}
                  </div>
                </CardHeader>
                
                <CardContent className="pt-0">
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start text-sm">
                        <CheckCircle2 className="h-4 w-4 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <Button 
                    className={`w-full text-lg py-6 font-bold ${
                      tier.popular 
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg' 
                        : 'bg-gray-100 hover:bg-blue-600 hover:text-white text-gray-900'
                    }`}
                  >
                    Start Streaming
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Add-Ons Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white mb-4 px-6 py-2">
              <Crown className="h-5 w-5 mr-2" />
              Premium Add-Ons
            </Badge>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Enhance Your Events
            </h3>
            <p className="text-xl text-gray-600">
              Optional upgrades to make your streams even more professional
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {addOns.map((addon, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-all duration-300 hover:scale-105">
                  <CardContent className="p-6 text-center">
                    <div className="text-2xl font-bold text-purple-600 mb-2">{addon.price}</div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">{addon.name}</h4>
                    <p className="text-gray-600 text-sm">{addon.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Volume Discounts */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-white text-center"
        >
          <Badge className="bg-white text-blue-600 mb-6 px-6 py-2 text-lg font-bold">
            <TrendingUp className="h-5 w-5 mr-2" />
            Volume Discounts
          </Badge>
          <h3 className="text-3xl md:text-4xl font-bold mb-6">
            Stream More, Save More
          </h3>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            The more events you stream, the more you save. Perfect for studios with multiple recitals per year.
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {volumeDiscounts.map((discount, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <div className="text-2xl font-bold mb-2">{discount.discount}</div>
                <div className="text-lg mb-2">{discount.events}</div>
                <div className="text-blue-200">Save {discount.savings}</div>
              </div>
            ))}
          </div>
          
          <Button 
            size="lg"
            className="bg-white text-blue-600 hover:bg-gray-100 mt-8 px-8 py-4 text-lg font-bold"
          >
            Calculate Your Savings
            <Calculator className="h-5 w-5 ml-2" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

export default LiveStreamPricing;